import React, { useRef, useEffect } from "react";

// React wrapper for Om Design System Checkbox web component
const OmCheckbox = React.forwardRef(({ 
  children,
  checked = false,
  disabled = false,
  indeterminate = false,
  onChange,
  onFocus,
  onBlur,
  id,
  name,
  value,
  required = false,
  ...props 
}, ref) => {
  const elementRef = useRef(null);

  useEffect(() => {
    const element = elementRef.current;
    if (!element) return;

    const addEventListeners = () => {
      const handleChange = (event) => {
        if (onChange) {
          // For Stencil web components, the checked value is on event.target.checked
          // Create a synthetic event that matches React's expected structure
          const syntheticEvent = {
            target: {
              checked: event.target.checked,
              name: element.name,
              value: element.value
            },
            currentTarget: element,
            preventDefault: () => event.preventDefault(),
            stopPropagation: () => event.stopPropagation()
          };
          
          console.log('🐛 DEBUG OmCheckbox change event:', {
            originalEvent: event,
            targetChecked: event.target.checked,
            elementChecked: element.checked,
            syntheticEvent
          });
          
          onChange(syntheticEvent);
        }
      };

      // Listen for both 'change' and 'input' events to ensure we catch all state changes
      const handleInput = (event) => {
        if (onChange) {
          // Some web components fire 'input' instead of 'change'
          const syntheticEvent = {
            target: {
              checked: event.target.checked,
              name: element.name,
              value: element.value
            },
            currentTarget: element,
            preventDefault: () => event.preventDefault(),
            stopPropagation: () => event.stopPropagation()
          };
          
          console.log('🐛 DEBUG OmCheckbox input event:', {
            originalEvent: event,
            targetChecked: event.target.checked,
            elementChecked: element.checked,
            syntheticEvent
          });
          
          onChange(syntheticEvent);
        }
      };

      const handleFocus = (event) => {
        if (onFocus) {
          onFocus(event);
        }
      };

      const handleBlur = (event) => {
        if (onBlur) {
          onBlur(event);
        }
      };

      try {
        element.addEventListener('change', handleChange);
        element.addEventListener('input', handleInput);
        element.addEventListener('focus', handleFocus);
        element.addEventListener('blur', handleBlur);
        
        return () => {
          if (element) {
            element.removeEventListener('change', handleChange);
            element.removeEventListener('input', handleInput);
            element.removeEventListener('focus', handleFocus);
            element.removeEventListener('blur', handleBlur);
          }
        };
      } catch (error) {
        console.warn('Failed to attach event listeners to OmCheckbox:', error);
        return () => {};
      }
    };

    // Check if component is ready or wait for it
    if (element.shadowRoot || element.isConnected) {
      return addEventListeners();
    } else {
      // Wait for component to be ready
      const timeout = setTimeout(addEventListeners, 100);
      return () => clearTimeout(timeout);
    }
  }, [onChange, onFocus, onBlur]);

  // Set checked state when prop changes
  useEffect(() => {
    const element = elementRef.current;
    if (element && element.checked !== checked) {
      element.checked = checked;
    }
  }, [checked]);

  // Set indeterminate state when prop changes
  useEffect(() => {
    const element = elementRef.current;
    if (element && element.indeterminate !== indeterminate) {
      element.indeterminate = indeterminate;
    }
  }, [indeterminate]);

  return (
    <omk-checkbox
      ref={(el) => {
        elementRef.current = el;
        if (ref) {
          if (typeof ref === 'function') {
            ref(el);
          } else {
            ref.current = el;
          }
        }
      }}
      id={id}
      name={name}
      value={value}
      checked={checked}
      disabled={disabled}
      indeterminate={indeterminate}
      required={required}
      {...props}
    >
      {children}
    </omk-checkbox>
  );
});

OmCheckbox.displayName = 'OmCheckbox';

export default OmCheckbox;
